// vm-standing.ts — THE VM STANDING DERIVATION, for BOTH surfaces (#4309).
//
// What a release's VM is doing right now, as text: the dot state
// (No VM / Starting / Verifying / Running / Stopping / Stopped, plus the
// Queued / VM gone / Unreachable / Stale-transition cells) and the count-up
// age that turns `Starting` into `Starting (42s)`.
//
// WHY IT LIVES HERE, in src/server/, and not beside the grid that draws it.
// This derivation was locked inside `src/client/home.ts` — 6262 lines with 70
// `document.`/`window.` references at c80ea9b3f — so nothing outside a browser
// could reach it. The proof that this was a real barrier and not a style
// preference: the only suite that tests these functions,
// `scripts/home.test.mjs`, has to BUNDLE home.ts with esbuild to import them
// (`import * as esbuild` at :10). A server-rendered page cannot run esbuild on
// its render path, so the no-VM interstitial (#4278 P4, #4312) could not say
// "Starting, 0:42" the way the grid does.
//
// PURE BY CONSTRUCTION, the same doctrine `vm-verb-table.ts` carries and for
// the same reason: no DB handle, no I/O, no DOM, no module-scope clock — the
// only time it reads is the `now` its callers pass, defaulting to Date.now()
// inside a function body. That is what makes it safe to bundle into the
// BROWSER (home.ts imports it exactly as it already imports vm-verb-table.js)
// AND to import under plain node on a server render path.
//
// ONE COPY, DELIBERATELY. `home.ts` CONSUMES this module and re-exports it;
// it holds no second copy of any derivation below. A transcription would pass
// its own tests forever while the grid diverged, which is the failure this
// extraction exists to prevent — so the grid and the interstitial are
// physically the same code, not two codes that agree today.
//
// NO BEHAVIOUR CHANGE. Every function below is the byte-identical body that
// ran in home.ts (and, for the three time primitives, in
// `src/client/relative-time.ts`, which now re-exports them from here so its
// own public surface is unchanged).

// ---------------------------------------------------------------------
// Time primitives — pure, and moved here rather than copied. They were in
// relative-time.ts beside two `window.setInterval` tickers, which is what
// made that module un-importable from a server.
// ---------------------------------------------------------------------

/**
 * Parse a server timestamp. Returns null for null/garbage rather than an
 * Invalid Date, so every caller has exactly one "no value" path.
 *
 * A bare (zone-less) stamp is read as UTC, which is what the server means.
 */
export function parseStamp(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const raw = String(iso);
  // Already zoned (Z, +hh:mm, -hh:mm) — trust it. Otherwise pin it to UTC.
  const zoned = /(?:Z|[+-]\d{2}:?\d{2})$/.test(raw);
  const d = new Date(zoned ? raw : `${raw.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** The absolute stamp, for the cell's `title` — relative time is friendlier to
 * read and useless for anything exact, so both ship and neither is lost. */
export function absoluteStamp(iso: string | null | undefined): string {
  const d = parseStamp(iso);
  if (!d) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/** VM-uptime tick cadence. One second, unlike TICK_MS: uptime COUNTS UPWARD
 * (owner request 2026-09-03) and shows seconds below the hour scale — a 30 s
 * repaint would read as a frozen counter for most of a young VM's life. The
 * painter is a handful of cells and a string format; the cost is noise. */
export const UPTIME_TICK_MS = 1_000;

/**
 * Elapsed time since `iso`, counting upward: "42s", "5m 3s", "3h 24m",
 * "2d 5h". Two units always (past the first minute) so neighbouring rows
 * compare at a glance; seconds drop out past the hour where they would be
 * churn without information. `now` is injectable so tests pin boundaries.
 *
 * A NEGATIVE elapsed (browser clock behind the server stamp) clamps to "0s"
 * — skew is not a future event, and an uptime column must never count down.
 * Null/garbage renders the em dash, the one "no value" shape everywhere.
 */
export function formatUptime(
  iso: string | null | undefined,
  now: number = Date.now(),
): string {
  const d = parseStamp(iso);
  if (!d) return '—';
  const s = Math.max(0, Math.floor((now - d.getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

// ---------------------------------------------------------------------
// The VM standing derivation itself.
// ---------------------------------------------------------------------

/** The VM column's whole decision (owner request 2026-09-03: "a column for
 * the VM status (like the test server status column)"), as one pure
 * function, from the ROW's own recorded facts — vm_instance_id,
 * vm_stopped_at, transition_state — never an EC2 call on the render path
 * (grid doctrine).
 *
 * Four states, the test-server column's shape:
 *   provisioning  a transition is in flight ('starting'/'gating', and
 *                 'shutting-down' labels itself "Stopping"). Wins over the
 *                 standing facts on purpose — the column reports what is
 *                 happening NOW, same rule as the deploying dot.
 *   running       instance recorded, no stopped stamp — the row truth the
 *                 proxy serves traffic by (stack-up doctrine: the row, not
 *                 the instance state, is the authority).
 *   stopped       instance recorded with vm_stopped_at set (idle-stop or a
 *                 lifecycle stop; Start — or the first RM message on a
 *                 vm-lane release — resumes it).
 *   none          no instance recorded. A MUTED dot, not a red one: no
 *                 machine is the normal state of a fresh release, never a
 *                 failure to alarm on. */
export interface VmCellModel {
  state: 'running' | 'provisioning' | 'stopped' | 'gone' | 'none' | 'queued' | 'unreachable' | 'corpus-failure';
  /** #3288: the three transition labels carry their AGE, so the template
   * members are open-ended — `Starting` alone is still legal and is what a
   * row with no `transition_since` renders (no stamp, no claim). */
  text:
    | 'Running' | `Starting${string}` | `Verifying${string}` | `Stopping${string}` | 'Stopped'
    | 'VM gone' | 'No VM' | 'Queued' | `Unreachable${string}` | `Stale transition${string}`
    | 'Corpus failure';
  cls: string;
  tip: string;
  /** #3288: this cell is an ACTION — the transition outlived its lane's
   * budget and POST /api/releases/:id/transition/reconcile is what ends it.
   * Absent on every other cell. */
  action?: 'reconcile';
}

/** #3288 — the server-published budget per transition lane, in SECONDS, as
 * GET /api/releases serves it (`transition_budgets`). ONE seat, server-side:
 * `starting`/`gating` carry the provisioner's own boot-lane wall
 * (`bootBudgetS + 600`) and `shutting-down` carries the stop watch's bound.
 *
 * A lane the map does not hold has NO budget and is never called stale — the
 * cell renders its age and stops. That is the honest answer for `cutting`
 * (no enforced wall) and for a console whose provisioner publishes none; an
 * invented client-side default would be the read layer lying, which is the
 * defect this whole lane exists to close. */
export interface TransitionBudgets { [state: string]: number }

/** #3288 — what a live transition's AGE means, decided in one place.
 *
 * Returns null when the row is not transitioning at all. `ageS` is null when
 * the row carries no `transition_since` (a pre-migration row, or a state set
 * by a console that predates the stamp): no stamp, no age, no staleness — the
 * cell renders the bare label exactly as it did before #3288, which is also
 * this change's PRE-FIX pin. */
export function transitionStanding(
  row: Pick<VmStandingFacts, 'transition_state' | 'transition_since'>,
  budgets?: TransitionBudgets | null,
  now: number = Date.now(),
): {
  state: string;
  ageS: number | null;
  budgetS: number | null;
  stale: boolean;
  /** Appended to the base label when there is an age to show ('' otherwise). */
  suffix: string;
  /** The WHOLE cell text when stale — the owner's sentence, verbatim. */
  staleText: `Stale transition${string}` | null;
  action: 'reconcile' | null;
  tip: string;
} | null {
  const state = row.transition_state;
  if (!state) return null;
  const d = parseStamp(row.transition_since ?? null);
  const ageS = d ? Math.max(0, Math.floor((now - d.getTime()) / 1000)) : null;
  const raw = budgets ? budgets[state] : undefined;
  const budgetS = typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : null;
  const stale = ageS !== null && budgetS !== null && ageS > budgetS;
  if (stale) {
    return {
      state, ageS, budgetS, stale: true,
      suffix: '',
      staleText: `Stale transition (${ageS}s) — click to reconcile`,
      action: 'reconcile',
      tip:
        `This release has read '${state}' for ${ageS}s, past the ${budgetS}s budget the console ` +
        `publishes for that lane — so no lane is still working on it (a console restart kills an ` +
        `in-flight gate). Click to RECONCILE: the console re-reads the recorded VM's boot report ` +
        `and writes its verdict, or clears the transition if the VM is gone. It never launches, ` +
        `stops, terminates or snapshots anything.`,
    };
  }
  if (ageS === null) {
    return {
      state, ageS: null, budgetS, stale: false, suffix: '', staleText: null, action: null,
      tip: 'this transition carries no start stamp, so there is no age to show (it predates the transition_since column)',
    };
  }
  return {
    state, ageS, budgetS, stale: false,
    suffix: ` (${formatUptime(row.transition_since ?? null, now)})`,
    staleText: null, action: null,
    tip: budgetS === null
      ? `in this state for ${ageS}s (this console publishes no budget for the '${state}' lane, so it is never called stale)`
      : `in this state for ${ageS}s of the ${budgetS}s the console publishes for that lane`,
  };
}

/** #4246 / #4250 (P1, owner scope 2026-09-18: "a failed corpus upgrade
 * reports release status 'Corpus Failure'") — THE ONE PREFIX. The console-
 * side corpus-upgrade job (P3) stamps `transition_error` with this prefix
 * followed by its per-epic breakdown line; every reader of that outcome —
 * the grid pill, the VM cell, `corpus_failure` on GET /api/releases/:id —
 * goes through isCorpusFailure() below and never a second string compare.
 * P3 IMPORTS this constant; a job that writes a different string is caught
 * by P1's own suite arm (a non-prefixed error must NOT read corpus failure).
 * Not a fifth `status` value: 26 literal readers and the DB CHECK stay
 * untouched (docs/plans/4246-corpus-upgrade-in-console.md, P1's premise). */
export const CORPUS_FAILURE_PREFIX = 'corpus upgrade failed: ';

/** #4246 P1 — is this row's last recorded failure a corpus-upgrade failure?
 * The ONE predicate; the release chrome, the grid and the API consume it. */
export function isCorpusFailure(row: { transition_error?: string | null }): boolean {
  return typeof row.transition_error === 'string' && row.transition_error.startsWith(CORPUS_FAILURE_PREFIX);
}

/** The row facts the derivation reads — the whole of its input, declared
 * STRUCTURALLY rather than picked off a grid row type, because the grid row
 * lives in the browser module and this one must not depend on it. A
 * `ReleaseGridRow` satisfies it structurally, which is what keeps the grid's
 * call sites unchanged; a server handler satisfies it from the same columns
 * `GET /api/releases` already selects. */
export interface VmStandingFacts {
  vm_instance_id: string | null;
  vm_stopped_at: string | null;
  vm_started_at?: string | null;
  vm_started_source?: string | null;
  transition_state?: string | null;
  transition_since?: string | null;
  /** #4246 P1: why the last accepted transition failed (migration 048). Read
   * here ONLY through isCorpusFailure() — a corpus-upgrade failure is a
   * DISPLAY state on this column, never a fifth `status`. */
  transition_error?: string | null;
  vm_state?: 'running' | 'stopped' | 'gone' | 'running-unreachable' | null;
  vm_gone_at?: string | null;
  vm_unreachable_since?: string | null;
  vm_unreachable_reason?: string | null;
  slot_queued_at?: string | null;
}

/** The name the moved bodies below use. */
type VmFacts = VmStandingFacts;

/** #3288 — one transition cell, for all three transition labels.
 *
 * `Starting` alone is what a row with no `transition_since` renders (no
 * stamp, no claim — and the PRE-FIX behaviour, so every arm written before
 * this change still measures what it measured). With a stamp the label
 * carries its AGE; past the lane's published budget the cell becomes the
 * owner's sentence and an ACTION, because a read layer that says `Starting`
 * at second 1 and at minute 13 is lying by omission. */
function transitionCell(
  row: VmFacts,
  label: 'Starting' | 'Verifying' | 'Stopping',
  baseTip: string,
  opts?: { budgets?: TransitionBudgets | null; now?: number },
): VmCellModel {
  const st = transitionStanding(row, opts?.budgets, opts?.now);
  if (st?.stale && st.staleText) {
    return {
      state: 'provisioning',
      text: st.staleText,
      cls: 'hc-ts hc-vm-provisioning hc-vm-stale',
      tip: `${baseTip}. ${st.tip}`,
      action: 'reconcile',
    };
  }
  return {
    state: 'provisioning',
    text: `${label}${st?.suffix ?? ''}`,
    cls: 'hc-ts hc-vm-provisioning',
    tip: st && st.ageS !== null ? `${baseTip} — ${st.tip}` : baseTip,
  };
}

export function vmStatusCell(
  row: VmFacts,
  /** #3288: the server-published lane budgets + an injectable clock. BOTH
   * optional, so every existing call site (and every existing arm) keeps its
   * exact behaviour: with no budgets nothing is ever stale, and with no
   * `transition_since` on the row there is not even an age. */
  opts?: { budgets?: TransitionBudgets | null; now?: number },
): VmCellModel {
  const t = row.transition_state;
  // #4246 P1 — CORPUS FAILURE, first in precedence (corpus-class error >
  // transition > slot queue > status): the owner's sentence is "a failed
  // corpus upgrade reports release status 'Corpus Failure'". The full error
  // text (P3's per-epic breakdown line) is the hover. Nothing below moves:
  // an accepted Start clears transition_error (releases.ts start()), and
  // the row then reads exactly what it read before this arm existed.
  if (isCorpusFailure(row)) {
    return {
      state: 'corpus-failure', text: 'Corpus failure', cls: 'hc-ts hc-vm-corpus-failure',
      tip: row.transition_error as string,
    };
  }
  // Waiting for a slot (migration 068): no pipeline owns the row yet — the
  // boot fires when the governor grants. Reported like a transition because
  // it is what is happening NOW; a real transition still wins below.
  if (!t && row.slot_queued_at) {
    return {
      state: 'queued', text: 'Queued', cls: 'hc-ts hc-vm-provisioning',
      tip: `Waiting for a VM slot since ${absoluteStamp(row.slot_queued_at)} — the boot fires automatically when a slot frees (see the slots counter)`,
    };
  }
  // GONE wins over the two standing facts (vm-reconcile, migration
  // 202609071000): the recorded instance no longer exists in EC2. Nobody in
  // this console stopped it, so vm_stopped_at is NULL and the rule below
  // would paint it "Running" — the phantom Active release measured on prod
  // 2026-09-07 (dev-env-drill-1 → i-0082c15f86858a8a2). A transition still
  // wins over it: a start pipeline that is re-provisioning reads Starting.
  // UNREACHABLE (#1107, migration 202609091624): EC2 says running, the box
  // does not answer — status check `impaired` and/or the Bridge silent for
  // N sweeps. REL-096 2026-09-09: the OOM-dead m7i.4xlarge read "Running"
  // here for hours. RED, with the time and the arm, and the remedy in the
  // tip. A transition still wins (a relaunch in flight reads Starting).
  if (row.vm_instance_id && row.vm_state === 'running-unreachable' && !(t === 'starting' || t === 'gating')) {
    const since = row.vm_unreachable_since ? absoluteStamp(row.vm_unreachable_since) : '?';
    const arm = /impaired/.test(row.vm_unreachable_reason ?? '')
      ? 'impaired'
      : (/no answer ×\d+/.exec(row.vm_unreachable_reason ?? '')?.[0] ?? 'no answer');
    return {
      state: 'unreachable', text: `Unreachable since ${since} (${arm})`, cls: 'hc-ts hc-vm-unreachable',
      tip:
        `VM ${row.vm_instance_id} is RUNNING per EC2 but not answering since ${since} — ` +
        `${row.vm_unreachable_reason ?? 'reason not recorded'}. Nothing reboots or terminates it automatically ` +
        `(the disk holds the forensics). Remedy: forensic snapshot → reboot for access → relaunch.`,
    };
  }
  if (row.vm_instance_id && row.vm_state === 'gone' && !(t === 'starting' || t === 'gating')) {
    const since = row.vm_gone_at ? ` since ${absoluteStamp(row.vm_gone_at)}` : '';
    return {
      state: 'gone', text: 'VM gone', cls: 'hc-ts hc-vm-gone',
      // #1272 (REL-096): relaunch REFUSES this cell by name ("nothing to
      // snapshot or terminate") — this tip used to name relaunch as the
      // remedy anyway, which is exactly the defect the row-menu fix closes.
      // An Active release's row menu now offers Provision VM for this cell
      // (vm/provision, the ordinary ensure pipeline re-provisions on the
      // current AMI); an Ideating one has no such button here yet and abort
      // is the only lever this grid gives it.
      tip:
        `VM ${row.vm_instance_id} is gone — terminated outside the console or absent from ` +
        `EC2${since}. An Active release's row menu re-provisions it fresh (Provision VM); ` +
        'otherwise abort the release.',
    };
  }
  if (t === 'starting' || t === 'gating') {
    const base = t === 'gating'
      ? 'Boot gate running — the VM is up and its boot report is being verified'
      : row.vm_instance_id
        ? `VM ${row.vm_instance_id} is starting`
        : 'A VM is being provisioned';
    return transitionCell(row, t === 'gating' ? 'Verifying' : 'Starting', base, opts);
  }
  if (t === 'shutting-down' && row.vm_instance_id) {
    return transitionCell(row, 'Stopping', `VM ${row.vm_instance_id} is shutting down`, opts);
  }
  if (row.vm_instance_id && !row.vm_stopped_at) {
    let since = ' (start time not recorded — the start predates the vm_started_at stamp)';
    if (row.vm_started_at) {
      // Migration 055: a backfilled/observed baseline is a DIFFERENT fact
      // than the console's own stamp, and the tip says which one it holds.
      since =
        row.vm_started_source === 'ec2-launch-time'
          ? ` since ${absoluteStamp(row.vm_started_at)} — EC2's own LaunchTime for this instance's LAST START (backfilled), not this console's start stamp`
          : row.vm_started_source === 'observed-running'
            ? ` since at least ${absoluteStamp(row.vm_started_at)} — observed already running at that instant`
            : row.vm_started_source === 'relaunch'
              ? ` since ${absoluteStamp(row.vm_started_at)} — RELAUNCHED onto this fresh instance (current AMI + bundle) by the console`
              // #1071: reconciled FROM EC2 because the instance was started
              // outside the console. Naming it is the point — falling through
              // to the bare stamp would render a silent correction, which is
              // the one thing the reconcile contract forbids.
              : row.vm_started_source === 'ec2-reconciled'
                ? ` since ${absoluteStamp(row.vm_started_at)} — EC2's own LaunchTime: this instance was STARTED OUTSIDE THE CONSOLE and the stamp was reconciled to match`
                : ` since ${absoluteStamp(row.vm_started_at)}`;
    }
    return {
      state: 'running', text: 'Running', cls: 'hc-ts hc-vm-running',
      tip: `VM ${row.vm_instance_id} — recorded running${since}`,
    };
  }
  if (row.vm_instance_id) {
    return {
      state: 'stopped', text: 'Stopped', cls: 'hc-ts hc-vm-stopped',
      tip:
        `VM ${row.vm_instance_id} is stopped — starting the release (or the first RM ` +
        `message on a vm-lane release) resumes it`,
    };
  }
  return {
    state: 'none', text: 'No VM', cls: 'hc-ts hc-vm-none',
    tip:
      'No VM is recorded for this release — one is provisioned at Start (or at the ' +
      'first RM message on a vm-lane release)',
  };
}

/** The uptime cell's decision: a ticking baseline ONLY while the VM is
 * running with a recorded start; every other shape is an em dash whose tip
 * NAMES why there is nothing to count (state, or the pre-051 unrecorded
 * start) — never a guessed uptime and never "0s" for missing data.
 *
 * `bound` (migration 055) is HOW the ticking number relates to the truth:
 * 'exact' = the console's own RunInstances/StartInstances stamp, OR a value
 * EC2 itself is exact about (an 'ec2-launch-time' backfill, an
 * 'ec2-reconciled' correction) — the tip then names the provenance instead
 * of an inequality;
 * 'lower' = the console observed the VM already running at the stamp (true
 * start is earlier, so the cell shows "≥");
 * 'upper' = a value whose true start is LATER than the stamp, so the cell
 * shows "≤". #1487: NO source produces 'upper' today. 'ec2-launch-time' did,
 * on the belief that a stop/start cycle leaves LaunchTime frozen at the
 * original launch — measured FALSE two-sided on 2026-09-10 (#1486/#1497),
 * LaunchTime advances on every start — so that shape is now 'exact' and the
 * member is kept for a source
 * that is genuinely an upper bound rather than deleted along with the
 * rendering that would be needed again. A backfilled number never renders as
 * if it were a real stamp: 'exact' is about the CLOCK, and the tip still
 * says whose start it was. */
export function vmUptimeCell(
  row: VmFacts,
): { iso: string | null; tip: string; bound: 'exact' | 'upper' | 'lower' | null } {
  const c = vmStatusCell(row);
  if (c.state === 'running' && row.vm_started_at) {
    // #1487. EXACT, not an upper bound: EC2's LaunchTime tracks the MOST
    // RECENT start (measured two-sided 2026-09-10, #1486/#1497 — a resumed
    // instance reads its resume, a never-resumed control reads its
    // provision), so this is the same instant the console would have stamped
    // had it made the start itself. The uncertainty is about WHO started the
    // machine and about what the clock is exact ABOUT — this start, not the
    // machine's whole life — so the tip says both instead of an inequality.
    if (row.vm_started_source === 'ec2-launch-time') {
      return {
        iso: row.vm_started_at,
        bound: 'exact',
        tip: `Counting since ${absoluteStamp(row.vm_started_at)} — EC2's own LaunchTime, ` +
          `backfilled because this console holds no start stamp for the machine. LaunchTime is ` +
          `this instance's LAST START, so the count is exact for the current start; the machine ` +
          `may have lived longer across earlier stop/start cycles than this clock shows`,
      };
    }
    if (row.vm_started_source === 'observed-running') {
      return {
        iso: row.vm_started_at,
        bound: 'lower',
        tip: `Running since at least ${absoluteStamp(row.vm_started_at)} — the console ` +
          `observed the VM already running at that instant; the true start is earlier, ` +
          `so this counts a lower bound (≥)`,
      };
    }
    if (row.vm_started_source === 'relaunch') {
      return {
        iso: row.vm_started_at,
        bound: 'exact',
        tip: `Counting since ${absoluteStamp(row.vm_started_at)} — the console RELAUNCHED this release ` +
          `onto a fresh instance (current AMI + bundle) at that instant; the previous machine was ` +
          `snapshotted and terminated, which is why the clock reset`,
      };
    }
    // #1071. EXACT, not bounded: this stamp is EC2's own LaunchTime for the
    // last start, which is the same instant the console would have recorded
    // had it made the start itself. The uncertainty this value carries is not
    // about the clock — it is about WHO started the machine, so the tip says
    // that instead of an inequality.
    if (row.vm_started_source === 'ec2-reconciled') {
      return {
        iso: row.vm_started_at,
        bound: 'exact',
        tip: `Counting since ${absoluteStamp(row.vm_started_at)} — EC2's own LaunchTime. This instance ` +
          `was STOPPED AND STARTED OUTSIDE THE CONSOLE, so the console's start stamp was stale; the ` +
          `vm-reconcile sweep corrected it and audited the drift (release.vm_state_reconciled)`,
      };
    }
    return {
      iso: row.vm_started_at,
      bound: 'exact',
      tip: `Counting since ${absoluteStamp(row.vm_started_at)} — the console's own ` +
        `RunInstances/StartInstances stamp, no EC2 call involved`,
    };
  }
  if (c.state === 'running') {
    return {
      iso: null,
      bound: null,
      tip: 'VM is running but its start predates the vm_started_at stamp (migration 051) — uptime unknown, not zero',
    };
  }
  return { iso: null, bound: null, tip: c.tip };
}
